/**
 * WorkspaceContext — owner of the analysis workspace state.
 *
 * Phase 3 (Navigation & IA): the provider is mounted by the persistent user
 * shell, above the routes. Navigating to /profile and back therefore no longer
 * throws away the selection, the weights, a running analysis or its results —
 * without any backend persistence. The logic below is the one that used to live
 * inside App.js: it was moved, not rewritten.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react";
import axios from "axios";
import { toast } from "sonner";

import { useKnownEtfs } from "@/hooks/useKnownEtfs";
import { useEtfWeights } from "@/hooks/useEtfWeights";
import { useEtfPasteJob } from "@/hooks/useEtfPasteJob";
import { useAnalysisJob } from "@/hooks/useAnalysisJob";
import {
  buildAllocationOverrides,
  captureAnalysisInput,
} from "@/utils/allocation";
import { selectionSignature, portfolioSignature } from "@/utils/analysisSignature";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Phase 36b — total ETF selection cap (paste + catalogue combined).
export const MAX_SELECTED_ETFS = 25;

const WorkspaceContext = createContext(null);

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error("useWorkspace must be used inside <WorkspaceProvider>");
  return ctx;
}

export function WorkspaceProvider({ children }) {
  // --- Analysis output state ---
  const [processing, setProcessing] = useState(false);
  const [processedData, setProcessedData] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [activeTab, setActiveTab] = useState("total-holdings");
  // When the analysis on screen was produced, and whether it was reopened from
  // a saved portfolio instead of just run. `signature` is the selection it
  // describes, so an edited selection can be flagged without discarding it.
  const [analysisMeta, setAnalysisMeta] = useState(null);
  // What to do with the NEXT result: replace a portfolio's snapshot, or ask for
  // a new portfolio. Set by "Ripeti analisi", consumed once the job lands.
  const saveIntentRef = useRef(null);
  const [saveDialogRequest, setSaveDialogRequest] = useState(false);
  // The portfolio the workspace is working on, when there is one: set both when
  // a portfolio is loaded and when one is saved. An analysis produced on ITS
  // selection is stored in it automatically, so saving the portfolio first and
  // analysing it later keeps the two together.
  const activePortfolioRef = useRef(null);

  // --- Selected ETFs (single source of truth) ---
  const [selectedEtfs, setSelectedEtfs] = useState([]);

  // Paste outcome banners
  const [pastePartial, setPastePartial] = useState([]);
  const [pasteFailed, setPasteFailed] = useState([]);

  // Phase 4 (A6): the "weights do not add up to 100%" warning is a product
  // dialog, not a browser prompt. The condition that allows the run is unchanged.
  const [weightConfirmOpen, setWeightConfirmOpen] = useState(false);

  const { knownEtfs, refreshKnownEtfs } = useKnownEtfs();
  const analysisJob = useAnalysisJob();
  const analysisInputRef = useRef(null);
  const analysisLocked = processing || analysisJob.isRunning;

  const selectedIsins = useMemo(
    () => selectedEtfs.map((e) => e.isin),
    [selectedEtfs],
  );

  const {
    weights, manualAllocation, totalWeight, isValid,
    setWeight, resetToEqual, loadWeights,
  } = useEtfWeights(selectedIsins);

  const addEtfIfMissing = useCallback((etf) => {
    if (analysisLocked) return false;
    if (!etf?.isin) return false;
    let added = false;
    setSelectedEtfs((prev) => {
      if (prev.some((e) => e.isin === etf.isin)) return prev;
      if (prev.length >= MAX_SELECTED_ETFS) return prev;
      added = true;
      return [...prev, etf];
    });
    return added;
  }, [analysisLocked]);

  /** Used by KnownEtfTable: toggle DB selection. Caps total at 25. */
  const handleSetSelectedIsins = useCallback(
    (isinsOrUpdater) => {
      if (analysisLocked) return;
      setSelectedEtfs((prev) => {
        const prevIsins = prev.map((e) => e.isin);
        const nextIsinsRaw = typeof isinsOrUpdater === "function"
          ? isinsOrUpdater(prevIsins)
          : isinsOrUpdater;
        const dedupedRaw = Array.from(new Set(nextIsinsRaw));
        const nextIsins = dedupedRaw.slice(0, MAX_SELECTED_ETFS);
        if (dedupedRaw.length > MAX_SELECTED_ETFS) {
          window.setTimeout(
            () => toast.warning(`Massimo ${MAX_SELECTED_ETFS} ETF selezionabili`),
            0,
          );
        }
        const nextSet = new Set(nextIsins);
        const kept = prev.filter((e) => nextSet.has(e.isin));
        const keptSet = new Set(kept.map((e) => e.isin));
        const added = nextIsins
          .filter((isin) => !keptSet.has(isin))
          .map((isin) => {
            const meta = knownEtfs.find((k) => k.isin === isin) || {};
            return {
              isin,
              ticker: meta.ticker || "",
              etf_name: meta.etf_name || isin,
              asset_type: meta.asset_type || "",
              source: "db",
            };
          });
        return [...kept, ...added];
      });
    },
    [analysisLocked, knownEtfs],
  );

  const atSelectionLimit = selectedEtfs.length >= MAX_SELECTED_ETFS;
  const notifyLimitHit = useCallback(() => {
    toast.warning(`Massimo ${MAX_SELECTED_ETFS} ETF selezionabili`);
  }, []);

  const removeEtf = useCallback((isin) => {
    if (analysisLocked) return;
    setSelectedEtfs((prev) => prev.filter((e) => e.isin !== isin));
  }, [analysisLocked]);

  // ===================================================================
  // Saved portfolios (Release 2)
  // ===================================================================
  /** Per-ETF freshness of a portfolio's snapshot, attached to the analysis. */
  const loadAnalysisDiff = useCallback(async (portfolioId) => {
    try {
      const { data } = await axios.get(
        `${API}/portfolios/${portfolioId}/analysis/diff`,
      );
      setAnalysisMeta((prev) => (
        prev && prev.portfolioId === portfolioId ? { ...prev, diff: data } : prev
      ));
    } catch {
      /* non-fatal: the results are shown without the freshness detail */
    }
  }, []);

  const loadPortfolio = useCallback(async (pf) => {
    if (analysisLocked) {
      toast.warning("Attendi il completamento dell’analisi in corso");
      return;
    }
    const catalogue = new Map(knownEtfs.map((e) => [e.isin, e]));
    const items = pf.items || [];
    const available = items.filter((i) => catalogue.has(i.isin));
    const missing = items.filter((i) => !catalogue.has(i.isin));

    if (available.length === 0) {
      toast.error("Nessuno degli ETF di questo portafoglio è più disponibile");
      return;
    }
    activePortfolioRef.current = {
      portfolioId: pf.portfolio_id,
      name: pf.name,
      signature: portfolioSignature(items),
    };
    setProcessedData(null);
    setSessionId(null);
    setAnalysisMeta(null);
    setPastePartial([]);
    setPasteFailed([]);
    analysisJob.reset();
    setSelectedEtfs(available.slice(0, MAX_SELECTED_ETFS).map((i) => {
      const meta = catalogue.get(i.isin) || {};
      return {
        isin: i.isin,
        ticker: meta.ticker || i.ticker || "",
        etf_name: meta.etf_name || i.etf_name || i.isin,
        asset_type: meta.asset_type || "",
        source: "db",
      };
    }));
    loadWeights(Object.fromEntries(
      available.map((i) => [i.isin, Number(i.weight_pct) || 0]),
    ));
    toast.success(`Portafoglio "${pf.name}" caricato`);
    if (missing.length > 0) {
      toast.warning(
        `${missing.length} ETF non più nel catalogo e non caricat${missing.length === 1 ? "o" : "i"}: `
        + missing.map((i) => i.isin).join(", "),
      );
    }

    // The saved analysis is reopened instead of re-run. Only a full portfolio
    // (nothing missing from the catalogue) still describes what is on screen.
    if (!pf.analysis || missing.length > 0) return;
    try {
      const { data } = await axios.post(
        `${API}/portfolios/${pf.portfolio_id}/analysis/restore`,
      );
      setSessionId(data.session_id);
      setProcessedData(data.result);
      setActiveTab("total-holdings");
      setAnalysisMeta({
        analyzedAt: data.analysis?.analyzed_at || null,
        portfolioId: pf.portfolio_id,
        portfolioName: pf.name,
        restored: true,
        signature: portfolioSignature(items),
        diff: null,
      });
      toast.success("Analisi salvata ripristinata");
      await loadAnalysisDiff(pf.portfolio_id);
    } catch (err) {
      if (err?.response?.status === 410) {
        toast.info("L'analisi salvata non è più disponibile: puoi rifarla");
      } else if (err?.response?.status !== 404) {
        toast.warning("Analisi salvata non ripristinata: puoi rifarla");
      }
    }
  }, [analysisLocked, knownEtfs, loadWeights, analysisJob, loadAnalysisDiff]);

  // ===================================================================
  // Paste flow (async job → 3 outcome buckets)
  // ===================================================================
  const handlePasteComplete = useCallback(({ full, partial, failed }) => {
    if (full && full.length > 0) {
      let addedCount = 0;
      let droppedDueToCap = 0;
      for (const r of full) {
        const ok = addEtfIfMissing({
          isin:       r.isin,
          ticker:     r.ticker || "",
          etf_name:   r.etf_name || r.isin,
          asset_type: r.asset_type || "",
          source:     "paste",
        });
        if (ok) addedCount++;
        else droppedDueToCap++;
      }
      if (addedCount > 0) {
        toast.success(`${addedCount} ETF importati e aggiunti al portafoglio`);
      }
      if (droppedDueToCap > 0 && selectedEtfs.length + addedCount >= MAX_SELECTED_ETFS) {
        toast.warning(
          `Limite di ${MAX_SELECTED_ETFS} ETF raggiunto — ${droppedDueToCap} ` +
          `ETF importat${droppedDueToCap === 1 ? "o" : "i"} ma non aggiunt${droppedDueToCap === 1 ? "o" : "i"} al portafoglio.`,
        );
      }
    }
    if (partial && partial.length > 0) {
      setPastePartial((prev) => [...prev, ...partial]);
    }
    if (failed && failed.length > 0) {
      setPasteFailed((prev) => [...prev, ...failed]);
    }
    refreshKnownEtfs();
  }, [addEtfIfMissing, selectedEtfs.length, refreshKnownEtfs]);

  const { pendingIsins, isBusy: pasteBusy, submitPaste } = useEtfPasteJob({
    onComplete: handlePasteComplete,
  });

  const handlePasteSubmit = useCallback(async (text) => {
    if (analysisLocked) {
      toast.warning("Attendi il completamento dell’analisi in corso");
      return { ok: false, error: "analysis-running" };
    }
    const ISIN_RE = /\b[A-Z]{2}[A-Z0-9]{9}\d\b/g;
    const matches = (text || "").toUpperCase().match(ISIN_RE) || [];
    const unique = Array.from(new Set(matches));
    if (unique.length === 0) {
      toast.error("Nessun ISIN valido trovato");
      return { ok: false };
    }

    const knownByIsin = new Map(knownEtfs.map((e) => [e.isin, e]));
    const alreadyKnown = unique.filter((i) => knownByIsin.has(i));
    const unknown = unique.filter((i) => !knownByIsin.has(i));

    let addedFastPath = 0;
    let droppedFastPath = 0;
    for (const isin of alreadyKnown) {
      const meta = knownByIsin.get(isin) || {};
      const ok = addEtfIfMissing({
        isin,
        ticker: meta.ticker || "",
        etf_name: meta.etf_name || isin,
        asset_type: meta.asset_type || "",
        source: "paste",
      });
      if (ok) addedFastPath++;
      else droppedFastPath++;
    }
    if (droppedFastPath > 0) {
      toast.warning(
        `Limite di ${MAX_SELECTED_ETFS} ETF raggiunto — ${droppedFastPath} ` +
        `ISIN ignorat${droppedFastPath === 1 ? "o" : "i"}.`,
      );
    }
    if (alreadyKnown.length > 0 && unknown.length === 0) {
      if (addedFastPath > 0) toast.success(`${addedFastPath} ETF aggiunti al portafoglio`);
      return { ok: true };
    }
    if (alreadyKnown.length > 0 && addedFastPath > 0) {
      toast.info(
        `${addedFastPath} ETF già nel catalogo aggiunti. ` +
        `Importazione di ${unknown.length} nuovi ISIN in corso…`,
      );
    }
    const res = await submitPaste(unknown.join("\n"));
    if (!res.ok) {
      toast.error(
        res.error === "busy"
          ? "Un import è già in corso, attendi che finisca"
          : res.error === "no-isin"
          ? "Nessun ISIN valido trovato"
          : (typeof res.error === "string" ? res.error : "Importazione ISIN fallita"),
      );
    }
    return res;
  }, [analysisLocked, knownEtfs, addEtfIfMissing, submitPaste]);

  // ===================================================================
  // Analysis pipeline (Phase 45 — async job)
  // ===================================================================
  useEffect(() => {
    const result = analysisJob.result;
    if (!result) return;
    let cancelled = false;
    (async () => {
      if (result.duplicate_etfs?.length > 0) {
        const names = result.duplicate_etfs
          .map((d) => d.etf_name || d.ticker).join(", ");
        toast.info(`ETF duplicati saltati: ${names}`);
      }
      let finalData = result;
      setSessionId(result.session_id);

      const analysisInput = analysisInputRef.current;
      const allocOverrides = buildAllocationOverrides({
        manualAllocation: analysisInput?.manualAllocation ?? false,
        allocationRows: result.allocation,
        selectedEtfs: analysisInput?.selectedEtfs || [],
        weights: analysisInput?.weights || {},
      });
      if (allocOverrides && Object.keys(allocOverrides).length > 0) {
        try {
          const { data: rec } = await axios.post(`${API}/recalculate`, {
            session_id: result.session_id,
            allocations: allocOverrides,
          });
          if (!cancelled) finalData = { ...result, ...rec };
        } catch (err) {
          toast.warning("Ribilanciamento non riuscito: mostro pesi uguali");
        }
      }
      if (cancelled) return;
      setProcessedData(finalData);
      setActiveTab("total-holdings");
      const explicit = saveIntentRef.current;
      saveIntentRef.current = null;
      const signature = selectionSignature(
        (analysisInput?.selectedEtfs || []).map((e) => e.isin),
        analysisInput?.weights || {},
      );
      // Nothing was declared, but the run describes exactly the selection of the
      // portfolio in hand: its snapshot is replaced instead of leaving the new
      // result loose. A different selection is never written silently.
      const active = activePortfolioRef.current;
      const intent = explicit || (
        active && signature && active.signature === signature
          ? {
              mode: "overwrite",
              portfolioId: active.portfolioId,
              portfolioName: active.name,
              implicit: true,
            }
          : null
      );
      setAnalysisMeta({
        analyzedAt: new Date().toISOString(),
        portfolioId: intent?.mode === "overwrite" ? intent.portfolioId : null,
        portfolioName: intent?.mode === "overwrite" ? intent.portfolioName : null,
        restored: false,
        signature,
        diff: null,
      });
      analysisInputRef.current = null;
      toast.success("Analisi completata");

      // "Ripeti analisi" on a saved portfolio replaces its snapshot: the old
      // analysis is gone, the new one takes its place.
      if (intent?.mode === "overwrite") {
        try {
          await axios.post(`${API}/portfolios`, {
            name: intent.portfolioName,
            items: (analysisInput?.selectedEtfs || []).map((e) => ({
              isin: e.isin,
              ticker: e.ticker || "",
              etf_name: e.etf_name || "",
              weight_pct: Number(analysisInput?.weights?.[e.isin] ?? 0),
            })),
            overwrite: true,
            session_id: result.session_id,
          });
          toast.success(intent.implicit
            ? `Analisi salvata in "${intent.portfolioName}"`
            : `Portafoglio "${intent.portfolioName}" aggiornato con la nuova analisi`);
          await loadAnalysisDiff(intent.portfolioId);
        } catch {
          toast.error("Analisi rifatta, ma il portafoglio non è stato aggiornato");
        }
      } else if (intent?.mode === "new") {
        setSaveDialogRequest(true);
      }
    })();
    return () => { cancelled = true; };
  }, [analysisJob.result, loadAnalysisDiff]);

  useEffect(() => {
    if (analysisJob.error) toast.error(analysisJob.error);
  }, [analysisJob.error]);

  const startAnalysis = useCallback(async () => {
    analysisInputRef.current = captureAnalysisInput({
      manualAllocation,
      selectedEtfs,
      weights,
    });
    setProcessing(true);
    try {
      const res = await analysisJob.start(selectedEtfs.map((e) => e.isin));
      if (!res.ok) {
        analysisInputRef.current = null;
        toast.error(res.error || "Avvio analisi fallito");
      }
    } finally {
      setProcessing(false);
    }
  }, [selectedEtfs, manualAllocation, weights, analysisJob]);

  /** Reason why "Esegui analisi" is not available, or null when it is. */
  const analysisBlockedReason = analysisLocked
    ? "Analisi in corso: attendi che finisca o annullala."
    : pasteBusy
    ? "Importazione ISIN in corso: attendi che finisca."
    : selectedEtfs.length === 0
    ? "Seleziona almeno un ETF dal catalogo o incollando gli ISIN."
    : null;

  const processFiles = useCallback(() => {
    if (analysisBlockedReason) return;
    if (!isValid) { setWeightConfirmOpen(true); return; }
    startAnalysis();
  }, [analysisBlockedReason, isValid, startAnalysis]);

  /**
   * Re-run the analysis, declaring what to do with the result:
   *   "overwrite" → replace the snapshot of the loaded portfolio
   *   "new"       → open "Salva portafoglio" once the new result is on screen
   *   "none"      → just re-run
   */
  const repeatAnalysis = useCallback((mode = "none") => {
    saveIntentRef.current = mode === "none" || !analysisMeta?.portfolioId
      ? (mode === "new" ? { mode: "new" } : null)
      : {
          mode,
          portfolioId: analysisMeta.portfolioId,
          portfolioName: analysisMeta.portfolioName,
        };
    processFiles();
  }, [analysisMeta, processFiles]);

  /**
   * A portfolio was just saved from the workspace: it becomes the portfolio in
   * hand, so a later run on the same selection updates it instead of leaving
   * the analysis loose.
   */
  const markPortfolioSaved = useCallback((portfolio, analysisSaved = false) => {
    if (!portfolio?.portfolio_id) return;
    activePortfolioRef.current = {
      portfolioId: portfolio.portfolio_id,
      name: portfolio.name,
      signature: portfolioSignature(portfolio.items || []),
    };
    if (!analysisSaved) return;
    setAnalysisMeta((prev) => (prev
      ? {
          ...prev,
          portfolioId: portfolio.portfolio_id,
          portfolioName: portfolio.name,
        }
      : prev));
    loadAnalysisDiff(portfolio.portfolio_id);
  }, [loadAnalysisDiff]);

  const downloadExcel = useCallback(async () => {    if (!sessionId) {
      toast.error("Nessun dato elaborato disponibile");
      return;
    }
    try {
      const { data } = await axios.get(`${API}/download/${sessionId}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "ETF_Holdings_Consolidated.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Download avviato");
    } catch {
      toast.error("Download del file fallito");
    }
  }, [sessionId]);

  const resetAll = useCallback(() => {
    if (analysisLocked) return;
    analysisInputRef.current = null;
    activePortfolioRef.current = null;
    setProcessedData(null);
    setSessionId(null);
    setAnalysisMeta(null);
    setSelectedEtfs([]);
    setPastePartial([]);
    setPasteFailed([]);
    analysisJob.reset();
  }, [analysisLocked, analysisJob]);

  const etfNames = useMemo(
    () => (processedData?.etf_data ? Object.keys(processedData.etf_data) : []),
    [processedData],
  );

  /** Does the analysis on screen still describe the current selection? */
  const analysisAligned = useMemo(() => {
    if (!analysisMeta?.signature) return true;
    return analysisMeta.signature === selectionSignature(selectedIsins, weights);
  }, [analysisMeta, selectedIsins, weights]);

  const value = {
    // analysis output
    processedData, setProcessedData, sessionId, activeTab, setActiveTab, etfNames,
    // selection
    selectedEtfs, selectedIsins, handleSetSelectedIsins, removeEtf,
    atSelectionLimit, notifyLimitHit, MAX_SELECTED_ETFS,
    // weights
    weights, manualAllocation, totalWeight, isValid, setWeight, resetToEqual,
    // catalogue + paste
    knownEtfs, refreshKnownEtfs, pendingIsins, pasteBusy, handlePasteSubmit,
    pastePartial, setPastePartial, pasteFailed, setPasteFailed,
    // jobs and actions
    analysisJob, analysisLocked, processFiles, downloadExcel, resetAll,
    loadPortfolio, analysisBlockedReason,
    analysisMeta, analysisAligned, repeatAnalysis, markPortfolioSaved,
    saveDialogRequest, clearSaveDialogRequest: () => setSaveDialogRequest(false),
    weightConfirmOpen, setWeightConfirmOpen, startAnalysis,
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export default WorkspaceProvider;
